import { DestroyRef, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { catchError, identity, map, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { HotToastService } from '@ngxpert/hot-toast';

import { branch } from '@core/branch';
import { environment } from '@environments/environment';
import { GlobalsService } from '@services/globals.service';
import { FinancingFormService } from '@services/financing-form.service';
import { ModalService } from '@services/modal.service';
import { CarValuationService } from './car-valuation.service';
import { OnlineFormService } from './online-form.service';
import {
  SaveFormRegistrationRequest,
  SaveFormRegistrationResponse,
  SaveFormValidationRequest,
  SaveFormValidationResponse,
  SaveFormGeneralInfoRequest,
  SaveFormGeneralInfoResponse,
  SaveFormGeneralInfoEditRequest,
  SaveFormGeneralInfoEditResponse,
  SaveFormSaveFinancingForm,
  SaveFormSubmitFormRequest,
  SaveFormGetUserIPResponse,
  SaveFormDataResponse,
  SaveFormSubmitFormRequestOld,
} from '@models/save-form.model';

@Injectable({
  providedIn: 'root',
})
export class SaveFormService {
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  private hotToastService = inject(HotToastService);
  private translateService = inject(TranslateService);
  private globalsService = inject(GlobalsService);
  private financingFormService = inject(FinancingFormService);
  private modalService = inject(ModalService);
  private carValuationService = inject(CarValuationService);
  private onlineFormService = inject(OnlineFormService);
  public formType: WritableSignal<string | undefined> = signal(undefined);
  public editCustomerData: WritableSignal<{ name?: string; email?: string; phone?: string } | undefined> = signal(undefined);

  registerUser(request: SaveFormRegistrationRequest): Observable<SaveFormRegistrationResponse | undefined> {
    const request$ = this.http.post<SaveFormRegistrationResponse>(`${environment.apiBeta74}/v1/garage/register/emailWithShareCode/`, request, { headers: this.globalsService.headerHash() });

    if (request.sendCode) {
      return request$.pipe(
        this.hotToastService.observe({
          loading: this.translateService.instant('forms.hotToast.shareCode.loading'),
          success: this.translateService.instant('forms.hotToast.shareCode.success'),
          error: this.translateService.instant('forms.hotToast.shareCode.error'),
        }),
        catchError((e: any) => {
          const timestamp: number = new Date().getTime();
          Sentry.captureMessage('Nepodařilo se odeslat shareCode - Timestamp: ' + timestamp + ' Error: ' + e.message, { level: 'fatal', extra: { body: request } });
          return of(undefined);
        }),
      );
    } else {
      return request$.pipe(
        catchError((e: any) => {
          const timestamp: number = new Date().getTime();
          Sentry.captureMessage('Nepodařilo se odeslat shareCode - Timestamp: ' + timestamp + ' Error: ' + e.message, { level: 'fatal', extra: { body: request } });
          return of(undefined);
        }),
      );
    }
  }

  validateUser(request: SaveFormValidationRequest): Observable<SaveFormValidationResponse | undefined> {
    return this.http.post<SaveFormValidationResponse>(`${environment.apiBeta74}/v1/garage/register/emailWithShareCode/validate/`, request, { headers: this.globalsService.headerHash() }).pipe(
      map((response) => {
        return response;
      }),
      this.hotToastService.observe({
        loading: this.translateService.instant('forms.hotToast.validate.loading'),
        success: this.translateService.instant('forms.hotToast.validate.success'),
        error: this.translateService.instant('forms.hotToast.validate.error'),
      }),
      catchError((e: any) => {
        const timestamp: number = new Date().getTime();
        Sentry.captureMessage('Nepodařilo se oveřit uživatele - Timestamp: ' + timestamp + ' Error: ' + e.message, { level: 'fatal', extra: { body: request } });
        return of(undefined);
      }),
    );
  }

  fetchForm(id: string): Observable<SaveFormDataResponse> {
    return this.http.get<SaveFormDataResponse>(`${environment.apiBeta74}/v1/garage/online-sales/${id}/`, { headers: this.globalsService.headerHashLogged() });
  }

  submitGeneralInfo(request: SaveFormGeneralInfoRequest, hotToasts: boolean): Observable<SaveFormGeneralInfoResponse | undefined> {
    return this.http.post<SaveFormGeneralInfoResponse>(`${environment.apiBeta74}/v1/forms/crm/general-info/`, request, { headers: this.globalsService.headerHashLogged() }).pipe(
      map((response) => {
        return response;
      }),
      hotToasts
        ? this.hotToastService.observe({
            loading: this.translateService.instant('forms.hotToast.sendForm.loading'),
            success: this.translateService.instant('forms.hotToast.sendForm.success'),
            error: this.translateService.instant('forms.hotToast.sendForm.error'),
          })
        : map(identity),
      catchError((e: any) => {
        const timestamp: number = new Date().getTime();
        Sentry.captureMessage('Nepodařilo se uložiť formulář - Timestamp: ' + timestamp + ' Error: ' + e.message, { level: 'fatal', extra: { body: request } });
        return of(undefined);
      }),
    );
  }

  editGeneralInfo(id: string, request: SaveFormGeneralInfoEditRequest): Observable<SaveFormGeneralInfoEditResponse | undefined> {
    return this.http.post<SaveFormGeneralInfoEditResponse>(`${environment.apiBeta74}/v1/forms/online-sales/${id}/edit`, request, { headers: this.globalsService.headerHashMD5() }).pipe(
      map((response) => {
        return response;
      }),
      catchError((e: any) => {
        const timestamp: number = new Date().getTime();
        Sentry.captureMessage('Nepodařilo se editovat formulář - Timestamp: ' + timestamp + ' Error: ' + e.message, { level: 'fatal', extra: { body: request } });
        return of(undefined);
      }),
    );
  }

  submitForm(submitFormRequest: SaveFormSubmitFormRequestOld): Observable<any> {
    return this.http.post<any>(`${environment.apiBeta74}/v1/forms/car-lease-calculation/`, submitFormRequest, { headers: this.globalsService.headerHash() }).pipe(
      this.hotToastService.observe({
        loading: this.translateService.instant('forms.hotToast.saveForm.loading'),
        success: this.translateService.instant('forms.hotToast.saveForm.success'),
        error: this.translateService.instant('forms.hotToast.saveForm.error'),
      }),
      catchError((e: any) => {
        const timestamp: number = new Date().getTime();
        Sentry.captureMessage('Nepodařilo se nahrat formulář - Timestamp: ' + timestamp + ' Error: ' + e.message, { level: 'fatal', extra: { body: submitFormRequest } });
        return of(undefined);
      }),
    );
  }

  getUserIp(): Observable<SaveFormGetUserIPResponse> {
    return this.http.get<SaveFormGetUserIPResponse>('https://api.ipify.org/?format=json');
  }

  editForm(formId: string | null, modalData?: { email?: string; phone?: string; phoneCode?: string; name?: string }): void {
    const financingFormData: SaveFormSaveFinancingForm = {
      carId: this.financingFormService.carContractData()?.carId,
      loanType: this.financingFormService.carContractData()?.loanType,
      numberOfMonths: this.financingFormService.carContractData()?.numberOfMonths,
      advance: this.financingFormService.carContractData()?.advance,
      product: this.financingFormService.product(),
      branch: this.financingFormService.step() === 3 ? branch : undefined,
      contactInformation: this.financingFormService.contactInfoData(),
    };
    if (formId) {
      const carFormDataString: string = this.globalsService.jsonToString(
        this.formType() === 'financingForm' ? financingFormData : this.formType() === 'carValuationForm' ? this.carValuationService.carValuationData() : this.formType() === 'cash' || this.formType() === 'installment' ? this.onlineFormService.formData() : {},
      );
      const phone: string | undefined = modalData?.phone !== undefined && modalData.phone !== this.editCustomerData()?.phone ? modalData.phone : this.editCustomerData()?.phone || modalData?.phone;
      const email: string | undefined = modalData?.email !== undefined && modalData.email !== this.editCustomerData()?.email ? modalData.email : this.editCustomerData()?.email || modalData?.email;
      const name: string | undefined = modalData?.name !== undefined && modalData.name !== this.editCustomerData()?.name ? modalData.name : this.editCustomerData()?.name || modalData?.name;

      const generalInfoRequest: SaveFormGeneralInfoEditRequest = {
        name: name ?? '',
        phone: phone,
        email: email,
        comment: 'Mototechna',
        formType: this.formType() === 'financingForm' ? 16 : this.formType() === 'carValuationForm' ? 4 : this.formType() === 'cash' ? 2 : this.formType() === 'installment' ? 1 : 0,
        upsale: '1',
        formUniqueId: this.globalsService.generateString(),
        formData: carFormDataString,
      };
      this.editGeneralInfo(formId, generalInfoRequest).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }
  }

  generalInfo(sendCode: boolean, generalInfoHotToast: boolean, modalData: { email?: string; phone?: string; phoneCode?: string; type?: boolean; name?: string }): Observable<SaveFormGeneralInfoResponse | null> {
    if (!modalData) return of(null);

    const financingFormData: SaveFormSaveFinancingForm = {
      carId: this.financingFormService.carContractData()?.carId,
      loanType: this.financingFormService.carContractData()?.loanType,
      numberOfMonths: this.financingFormService.carContractData()?.numberOfMonths,
      advance: this.financingFormService.carContractData()?.advance,
      product: this.financingFormService.product(),
      branch: this.financingFormService.step() === 3 ? branch : undefined,
      contactInformation: this.financingFormService.contactInfoData(),
    };

    const formDataString: string = this.globalsService.jsonToString(
      this.formType() === 'financingForm' ? financingFormData : this.formType() === 'carValuationForm' ? this.carValuationService.carValuationData() : this.formType() === 'cash' || this.formType() === 'installment' ? this.onlineFormService.formData() : {},
    );
    const phone: string | undefined = modalData.phone ?? undefined;

    const email: string | undefined = modalData.type ? undefined : modalData.email;

    const phoneCodeNumber: number | undefined = modalData.phoneCode ? Number(modalData.phoneCode.replace('+', '')) : undefined;
    const phoneCountry: number | undefined = this.globalsService.searchPhoneCountryCode(phoneCodeNumber);

    const body: SaveFormGeneralInfoRequest = {
      name: modalData.name ?? '',
      phoneCountryId: phoneCountry,
      phone: phone,
      email: email,
      sendSMS: modalData.type,
      comment: 'Mototechna',
      formType: this.formType() === 'financingForm' ? 16 : this.formType() === 'carValuationForm' ? 4 : this.formType() === 'cash' ? 2 : this.formType() === 'installment' ? 1 : 0,
      formUniqueId: this.globalsService.generateString(),
      formData: formDataString,
    };

    return this.submitGeneralInfo(body, generalInfoHotToast).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((response) => {
        if (!response) {
          return null;
        }
        if (this.formType() === 'financingForm') this.financingFormService.formId.set(response.message.formId);
        else if (this.formType() === 'carValuationForm') this.carValuationService.formId.set(response.message.formId);
        else if (this.formType() === 'cash' || this.formType() === 'installment') this.onlineFormService.formId.set(response.message.formId);
        if (sendCode) this.modalService.close();
        return response;
      }),
    );
  }
}
